/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import PropTypes from "prop-types";

const getRoleLabel = (user) => {
  if (!user) return null;
  const raw = user.role;
  if (!raw) return null;
  const n = typeof raw === "string" ? raw : raw?.name || null;
  return n ? n.charAt(0).toUpperCase() + n.slice(1).toLowerCase() : null;
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  .bc-header {
    position: fixed; top: 0; left: 0; right: 0;
    height: 60px; z-index: 1000;
    background: #fff;
    border-bottom: 1.5px solid #f0f0f5;
    box-shadow: 0 1px 8px rgba(0,0,0,.06);
    display: flex; align-items: center;
    padding: 0 20px; gap: 12px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    box-sizing: border-box;
  }

  /* Hamburger — always visible on all screen sizes */
  .bc-hamburger {
    width: 36px; height: 36px; flex-shrink: 0;
    background: none; border: none; cursor: pointer; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    color: #6b7280;
    transition: background .18s, color .18s;
    padding: 0;
  }
  .bc-hamburger:hover { background: #FDF3E3; color: #7B4D12; }
  .bc-hamburger svg {
    width: 20px; height: 20px;
    stroke: currentColor; fill: none; stroke-width: 2;
    stroke-linecap: round;
  }

  .bc-logo {
    display: flex; align-items: center; gap: 8px;
    text-decoration: none; flex-shrink: 0;
  }
  .bc-logo img {
    height: 32px;
    width: auto;
    display: block;
  }
  @media (max-width: 480px) { 
    .bc-logo img { 
      height: 28px;
    }
  }

  .bc-spacer { flex: 1; }

  /* User button */
  .bc-user-btn {
    display: flex; align-items: center; gap: 8px;
    background: none; border: 1.5px solid #e5e7eb; border-radius: 10px;
    padding: 5px 10px; cursor: pointer;
    transition: border-color .18s, background .18s;
    position: relative; flex-shrink: 0;
  }
  .bc-user-btn:hover { border-color: #7B4D12; background: #FDF3E3; }

  .bc-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    color: #fff; font-size: 11px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .bc-avatar--admin    { background: linear-gradient(135deg, #7B4D12, #8B5E1A); }
  .bc-avatar--customer { background: linear-gradient(135deg, #6B4226, #8B6340); }

  .bc-user-info { text-align: left; }
  .bc-user-name { font-size: 13px; font-weight: 600; color: #1a1d2e; line-height: 1.3; }

  .bc-role-pill {
    display: inline-flex; align-items: center;
    font-size: 10px; font-weight: 600; letter-spacing: .03em;
    padding: 1px 6px; border-radius: 20px; margin-top: 1px;
  }
  .bc-role-pill--admin    { background: #F5ECD7; color: #7B4D12; }
  .bc-role-pill--customer { background: #F5EAD5; color: #6B4226; }

  .bc-chevron {
    width: 14px; height: 14px; stroke: #9ca3af; fill: none; stroke-width: 2;
    transition: transform .18s;
  }
  .bc-user-btn:hover .bc-chevron { transform: rotate(180deg); stroke: #7B4D12; }
  @media (max-width: 480px) { .bc-user-info { display: none; } }

  /* Dropdown */
  .bc-dropdown {
    position: absolute; top: calc(100% + 8px); right: 0;
    background: #fff; border: 1.5px solid #f0f0f5; border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,.12); min-width: 170px;
    z-index: 1010; overflow: hidden;
    animation: bc-drop .18s ease both;
  }
  @keyframes bc-drop {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .bc-dd-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px; font-size: 13.5px; font-weight: 500;
    color: #374151; cursor: pointer; text-decoration: none;
    transition: background .15s, color .15s;
    width: 100%; border: none; background: none;
    font-family: inherit; text-align: left;
  }
  .bc-dd-item:hover { background: #FDF3E3; color: #7B4D12; }
  .bc-dd-item svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; flex-shrink: 0; }
  .bc-dd-divider { height: 1px; background: #f3f4f6; margin: 4px 0; }
  .bc-dd-danger { color: #ef4444 !important; }
  .bc-dd-danger:hover { background: #fff1f2 !important; }
`;

// ══════════════════════════════════════════════════════════════════════════════
const Header = ({ onMenuClick }) => {
  const history = useHistory();
  const [showDD, setShowDD] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const ddRef = useRef(null);

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const roleLabel = getRoleLabel(user);
  const isAdmin = roleLabel?.toLowerCase() === "admin";
  const displayName = user
    ? user.firstName || user.lastName
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : user.email || "User"
    : "User";
  const initials =
    displayName
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 481);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setShowDD(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    history.replace("/login");
  };

  // ── The hamburger calls onMenuClick from the page (legacy toggle),
  //    AND also fires a custom event that Sidebar listens to.
  //    This way all existing pages work without any changes.
  const handleHamburger = () => {
    // Call the page's existing handler (toggles slide-nav class, harmless)
    if (onMenuClick) onMenuClick();
    // Fire a custom event that Sidebar listens to
    window.dispatchEvent(new CustomEvent("bc-toggle-sidebar"));
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="bc-header">
        {/* Hamburger — always visible */}
        <button
          className="bc-hamburger"
          onClick={handleHamburger}
          aria-label="Toggle navigation"
        >
          <svg viewBox="0 0 24 24">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Logo - Image from public folder */}
        <div className="bc-logo">
          <img src="/EF-logo-dark.png" alt="EF Logo" />
        </div>

        <div className="bc-spacer" />

        {/* User menu */}
        <div ref={ddRef} style={{ position: "relative" }}>
          <button className="bc-user-btn" onClick={() => setShowDD((v) => !v)}>
            <div
              className={`bc-avatar ${
                isAdmin ? "bc-avatar--admin" : "bc-avatar--customer"
              }`}
            >
              {initials}
            </div>
            {!isMobile && (
              <div className="bc-user-info">
                <div className="bc-user-name">{displayName}</div>
                {roleLabel && (
                  <span
                    className={`bc-role-pill ${
                      isAdmin ? "bc-role-pill--admin" : "bc-role-pill--customer"
                    }`}
                  >
                    {roleLabel}
                  </span>
                )}
              </div>
            )}
            <svg className="bc-chevron" viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showDD && (
            <div className="bc-dropdown">
              <Link
                to="/profile"
                className="bc-dd-item"
                onClick={() => setShowDD(false)}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                My Profile
              </Link>
              <div className="bc-dd-divider" />
              <button
                className="bc-dd-item bc-dd-danger"
                onClick={handleLogout}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

Header.propTypes = { onMenuClick: PropTypes.func.isRequired };
export default Header;
